// Anlegen + Bearbeiten eines Mandanten in einem gemeinsamen Modal.
//
// Bei "create": Kundennr ist bearbeitbar.
// Bei "edit":  Kundennr ist read-only (Identitaet, nicht aenderbar —
//              siehe repository.update_mandant).

import { useState, type FormEvent } from "react";

import type { Mandant } from "@/api/types";
import { useCreateMandant, useUpdateMandant } from "@/api/hooks";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";

interface Props {
  // Wenn `mandant` gesetzt ist, wird editiert; sonst neu angelegt.
  mandant?: Mandant;
  onClose: () => void;
}

export function MandantForm({ mandant, onClose }: Props) {
  const isEdit = !!mandant;
  const toast = useToast();
  const create = useCreateMandant();
  const update = useUpdateMandant(mandant?.id ?? "");

  const [bmdKundennr, setBmdKundennr] = useState(mandant?.bmd_kundennr ?? "");
  const [name, setName] = useState(mandant?.name ?? "");
  const [vorname, setVorname] = useState(mandant?.vorname ?? "");
  const [steuernr, setSteuernr] = useState(mandant?.steuernr_fa ?? "");
  const [plz, setPlz] = useState(mandant?.plz ?? "");
  const [ort, setOrt] = useState(mandant?.ort ?? "");
  const [strasse, setStrasse] = useState(mandant?.strasse ?? "");
  const [sachbearbNr, setSachbearbNr] = useState(mandant?.sachbearb_nr ?? "");
  const [isWp, setIsWp] = useState<boolean>(mandant?.is_wp ?? false);
  const [varianten, setVarianten] = useState(
    (mandant?.name_varianten ?? []).join("; ")
  );
  const [domains, setDomains] = useState(
    (mandant?.email_domains ?? []).join("; ")
  );
  const [aktiv, setAktiv] = useState(mandant?.aktiv ?? true);

  const busy = create.isPending || update.isPending;

  const splitList = (raw: string): string[] =>
    raw
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean);

  const orNull = (s: string) => (s.trim() === "" ? null : s.trim());

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const sharedFields = {
      name: name.trim(),
      vorname: orNull(vorname),
      steuernr_fa: orNull(steuernr),
      plz: orNull(plz),
      ort: orNull(ort),
      strasse: orNull(strasse),
      sachbearb_nr: orNull(sachbearbNr),
      is_wp: isWp,
      name_varianten: splitList(varianten),
      email_domains: splitList(domains),
      aktiv,
    };
    if (isEdit) {
      update.mutate(sharedFields, {
        onSuccess: (m) => {
          toast.success(`Mandant "${m.name}" gespeichert.`);
          onClose();
        },
        onError: (err) =>
          toast.error(`Speichern fehlgeschlagen: ${err.message}`),
      });
      return;
    }
    create.mutate(
      { bmd_kundennr: bmdKundennr.trim(), ...sharedFields },
      {
        onSuccess: (m) => {
          toast.success(`Mandant "${m.name}" angelegt.`);
          onClose();
        },
        onError: (err) =>
          toast.error(`Anlegen fehlgeschlagen: ${err.message}`),
      }
    );
  };

  return (
    <Modal
      title={isEdit ? `Mandant bearbeiten` : "Mandant anlegen"}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={busy}
            disabled={!bmdKundennr.trim() || !name.trim()}
          >
            {isEdit ? "Speichern" : "Anlegen"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="bmd-stack-4">
        <div>
          <label className="bmd-label" htmlFor="m-kundennr">
            BMD-Kundennr
          </label>
          <input
            id="m-kundennr"
            className="bmd-input"
            value={bmdKundennr}
            onChange={(e) => setBmdKundennr(e.target.value)}
            disabled={isEdit}
            placeholder="z. B. 10001"
            autoFocus={!isEdit}
            required
          />
          {isEdit && (
            <div className="bmd-subtle" style={{ fontSize: 12, marginTop: 4 }}>
              Kundennr ist nicht änderbar — neue Nummer = neuer Mandant.
            </div>
          )}
        </div>

        <div className="bmd-form-grid">
          <div>
            <label className="bmd-label" htmlFor="m-name">
              Name / Firma
            </label>
            <input
              id="m-name"
              className="bmd-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z. B. Muster Wirtschaftsprüfung GmbH"
              autoFocus={isEdit}
              required
            />
          </div>

          <div>
            <label className="bmd-label" htmlFor="m-vorname">
              Vorname <span className="bmd-subtle">(nur Privatkunden)</span>
            </label>
            <input
              id="m-vorname"
              className="bmd-input"
              value={vorname}
              onChange={(e) => setVorname(e.target.value)}
              placeholder="z. B. Gerhard"
            />
          </div>
        </div>

        <div>
          <label className="bmd-label" htmlFor="m-steuernr">
            Steuernr. FA
          </label>
          <input
            id="m-steuernr"
            className="bmd-input"
            value={steuernr}
            onChange={(e) => setSteuernr(e.target.value)}
            placeholder="z. B. 08 309/5331"
          />
          <div className="bmd-subtle" style={{ fontSize: 12, marginTop: 4 }}>
            Hilft dem Bot extrem — Steuernr. ist auf jedem Beleg eindeutig.
          </div>
        </div>

        <div className="bmd-form-grid">
          <div>
            <label className="bmd-label" htmlFor="m-plz">PLZ</label>
            <input
              id="m-plz"
              className="bmd-input"
              value={plz}
              onChange={(e) => setPlz(e.target.value)}
              placeholder="1130"
            />
          </div>
          <div>
            <label className="bmd-label" htmlFor="m-ort">Ort</label>
            <input
              id="m-ort"
              className="bmd-input"
              value={ort}
              onChange={(e) => setOrt(e.target.value)}
              placeholder="Wien"
            />
          </div>
        </div>

        <div>
          <label className="bmd-label" htmlFor="m-strasse">Straße</label>
          <input
            id="m-strasse"
            className="bmd-input"
            value={strasse}
            onChange={(e) => setStrasse(e.target.value)}
            placeholder="Hietzinger Kai 89"
          />
        </div>

        <div className="bmd-form-grid">
          <div>
            <label className="bmd-label" htmlFor="m-sachbearb">
              SachbearbNr
            </label>
            <input
              id="m-sachbearb"
              className="bmd-input"
              value={sachbearbNr}
              onChange={(e) => setSachbearbNr(e.target.value)}
              placeholder="2001"
            />
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 8 }}>
            <label className="bmd-mandanten-toggle">
              <input
                type="checkbox"
                checked={isWp}
                onChange={(e) => setIsWp(e.target.checked)}
              />
              <span>WP-Mandant</span>
            </label>
          </div>
        </div>

        <div>
          <label className="bmd-label" htmlFor="m-varianten">
            Namens-Varianten <span className="bmd-subtle">(Semikolon-getrennt)</span>
          </label>
          <input
            id="m-varianten"
            className="bmd-input"
            value={varianten}
            onChange={(e) => setVarianten(e.target.value)}
            placeholder="Muster; Muster GmbH; Muster Steuerberatung"
          />
        </div>

        <div>
          <label className="bmd-label" htmlFor="m-domains">
            E-Mail-Domains <span className="bmd-subtle">(Semikolon-getrennt)</span>
          </label>
          <input
            id="m-domains"
            className="bmd-input"
            value={domains}
            onChange={(e) => setDomains(e.target.value)}
            placeholder="muster.at; mandant-mail.example"
          />
        </div>

        <div>
          <label className="bmd-mandanten-toggle">
            <input
              type="checkbox"
              checked={aktiv}
              onChange={(e) => setAktiv(e.target.checked)}
            />
            <span>Aktiv (wird vom Bot als Match-Kandidat berücksichtigt)</span>
          </label>
        </div>

        {/* Hidden submit, damit Enter im Input absendet */}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
